import { useCallback, useMemo, useState } from "react";
import type { LeadSubmissionPublic } from "@dugads/shared";
import { Button } from "@/components/ui/button";
import {
  DataTable, applyClientListParams,
  type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import { CheckCircle2, XCircle } from "lucide-react";

interface LeadSubmissionsTableProps {
  leads: LeadSubmissionPublic[];
  total: number;
  /** Which context column to show: "school" on sponsor pages, "sponsor" on school pages */
  contextColumn: "school" | "sponsor";
}

/**
 * Lead submissions list, re-based onto the shared <DataTable> (182). The leads
 * are fetched and passed in by the host page; this component owns search / sort
 * / pagination (client-side via applyClientListParams) plus the success/failed
 * status filter (a page-local filter the data is small enough to apply here).
 */
export function LeadSubmissionsTable({ leads, total, contextColumn }: LeadSubmissionsTableProps) {
  const [statusFilter, setStatusFilter] = useState<"all" | "success" | "failed">("all");

  const successCount = useMemo(() => leads.filter((l) => l.success).length, [leads]);
  const failedCount = leads.length - successCount;

  const fetcher = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: LeadSubmissionPublic[]; total: number }> => {
      let rows = leads;
      if (statusFilter === "success") rows = rows.filter((l) => l.success);
      else if (statusFilter === "failed") rows = rows.filter((l) => !l.success);
      return applyClientListParams(rows, params, {
        searchFields: (l) => [
          `${l.first_name} ${l.surname}`,
          l.mobile,
          l.school_name,
          l.sponsor_name,
          l.provider_reference,
        ],
        sortAccessors: {
          name: (l) => `${l.first_name} ${l.surname}`,
          mobile: (l) => l.mobile,
          school: (l) => l.school_name ?? "",
          sponsor: (l) => l.sponsor_name ?? "",
          status: (l) => Number(l.success),
          reference: (l) => l.provider_reference ?? "",
          date: (l) => l.created_at,
        },
        defaultSort: "date",
      });
    },
    [statusFilter, leads],
  );

  const columns: DataTableColumn<LeadSubmissionPublic>[] = [
    { key: "name", header: "Name", sortable: true, className: "text-sm", render: (l) => `${l.first_name} ${l.surname}` },
    { key: "mobile", header: "Mobile", sortable: true, className: "text-sm text-muted-foreground", render: (l) => l.mobile },
    {
      key: contextColumn,
      header: contextColumn === "school" ? "School" : "Sponsor",
      sortable: true,
      className: "text-sm text-muted-foreground",
      render: (l) => (contextColumn === "school" ? (l.school_name ?? "--") : (l.sponsor_name ?? "--")),
    },
    {
      key: "status", header: "Status", sortable: true,
      render: (l) =>
        l.success ? (
          <span className="flex items-center gap-1 text-green-600 text-xs"><CheckCircle2 className="h-3 w-3" /> Success</span>
        ) : (
          <span className="flex items-center gap-1 text-destructive text-xs"><XCircle className="h-3 w-3" /> Failed</span>
        ),
    },
    { key: "reference", header: "Reference", sortable: true, className: "text-xs text-muted-foreground", render: (l) => l.provider_reference ?? "--" },
    { key: "date", header: "Date", sortable: true, className: "text-xs text-muted-foreground", render: (l) => new Date(l.created_at).toLocaleString() },
  ];

  const statusFilterControls = (
    <div className="flex gap-1">
      <Button variant={statusFilter === "all" ? "default" : "outline"} size="sm" className="h-7 text-xs" onClick={() => setStatusFilter("all")}>
        All ({total})
      </Button>
      <Button variant={statusFilter === "success" ? "default" : "outline"} size="sm" className="h-7 text-xs" onClick={() => setStatusFilter("success")}>
        Success ({successCount})
      </Button>
      <Button variant={statusFilter === "failed" ? "default" : "outline"} size="sm" className="h-7 text-xs" onClick={() => setStatusFilter("failed")}>
        Failed ({failedCount})
      </Button>
    </div>
  );

  return (
    <DataTable
      columns={columns}
      fetcher={fetcher}
      fetchKey={`${statusFilter}:${leads.length}:${total}`}
      rowKey={(l) => l.id}
      searchPlaceholder="Search name, mobile, reference..."
      toolbarExtra={statusFilterControls}
      defaultSort="date"
      defaultOrder="desc"
      emptyMessage="No leads submitted yet."
    />
  );
}
